/*
 * A COMPATIBILIDADE não é selo nem filtro da grade (AB#85634).
 *
 * Ela é derivada do pacote e gravada na skill, e o contrato da api entrega
 * `compatibility`. Na tela, aparece só como uma linha no detalhe da skill,
 * dizendo onde os scripts rodam (AB#85642) — não no cartão, na barra ou na
 * revisão do importador.
 *
 * Por que: ela era informação solta. Dizia "esta skill traz scripts" e mais
 * nada — não impedia instalar, não escolhia quem recebe, e obrigava quem olha a
 * saber o que é um harness para tirar alguma conclusão. A leitura de risco e de
 * requisitos passa a ser trabalho da VARREDURA, no PBI próprio dela: lá a
 * compatibilidade entra como um item de uma lista de verificações, ao lado dos
 * demais, com o resultado explicado — que é onde ela informa alguma coisa.
 */

import type { SkillStorefrontEntry } from "../types/skill";

/** O recorte da grade: tudo, só o que dá para instalar, ou só o que já está. */
export type SkillsFilterMode = "all" | "store" | "installed";

export interface SkillsFilterState {
  query: string;
  mode: SkillsFilterMode;
  /** Etiquetas escolhidas, no formato `nome:valor` — o mesmo do pacote. */
  facets: string[];
}

export const FILTRO_VAZIO: SkillsFilterState = {
  query: "",
  mode: "all",
  facets: [],
};

/**
 * Aplica o filtro à grade do catálogo (AB#85634).
 *
 * Tudo no cliente, de propósito: a vitrine já vem inteira numa resposta só, e
 * levar busca e filtro para a api trocaria um `filter` instantâneo por uma ida à
 * rede a cada tecla. Se o catálogo crescer a ponto de não caber numa resposta, é
 * a paginação que muda primeiro — e aí o filtro vai junto.
 *
 * Mora fora do componente porque a barra é uma tela e isto é regra: misturar os
 * dois no mesmo arquivo quebra o hot reload da barra durante o desenvolvimento.
 */
export const filtrarSkills = (
  skills: SkillStorefrontEntry[],
  filtro: SkillsFilterState,
): SkillStorefrontEntry[] => {
  const termo = filtro.query.trim().toLowerCase();

  return skills.filter((skill) => {
    if (filtro.mode === "installed" && !skill.installed) return false;
    // "Loja" é o que ainda dá para trazer para cá — e não o catálogo inteiro,
    // que é o que "Todas" mostra.
    if (filtro.mode === "store" && skill.installed) return false;

    if (filtro.facets.length > 0) {
      const daSkill = new Set(skill.facets ?? []);
      // Todas as escolhidas precisam estar presentes: um filtro que casasse com
      // QUALQUER uma alargaria o resultado a cada clique, que é o contrário do
      // que se espera ao acrescentar um filtro.
      if (!filtro.facets.every((facet) => daSkill.has(facet))) return false;
    }

    if (!termo) return true;

    // Busca por nome, título, assunto e etiqueta. A descrição entra porque é
    // onde a skill diz QUANDO usá-la, e é assim que alguém a procura quando não
    // lembra o nome.
    const alvo = [skill.title, skill.name, skill.description, ...(skill.facets ?? [])]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return alvo.includes(termo);
  });
};
